import { useEffect } from 'react';

/**
 * Holds `<meta name="robots" content="noindex">` on the document while mounted.
 *
 * For the surfaces that must never reach a search index: the audience routes,
 * whose URLs are the poll itself, and the not-found view, which every unknown
 * path renders. That last one carries the most weight today — CloudFront
 * rewrites 404 to 200 so client-side routing survives a hard refresh, which
 * means every invented URL answers with a page, and until that rewrite grows a
 * known-route allowlist this tag is the only thing keeping an unbounded space
 * of soft 404s out of the index.
 *
 * It removes the tag on unmount rather than leaving it: these are all
 * client-side routes, so a visitor who lands on a join code and then navigates
 * home would otherwise carry noindex onto the one page that wants indexing.
 *
 * The element is created per mount and removed by the same closure, so two
 * components using this at once cannot delete each other's tag — duplicate
 * noindex tags are harmless, a prematurely removed one is not.
 */
export function useNoIndex(): void {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
}
